/**
 * @fileoverview DomLoader for loading DOM manipulation and UI-related modules.
 * 
 * This loader handles modules that are responsible for DOM manipulation, UI components,
 * interactive elements, and client-side functionality that directly interacts with
 * the document object model.
 * 
 * @author Roshan Jose
 * @version 2.0.0
 * @since 2025
 */

import { RouterHelper } from "../router-helper";
import { IModuleLoader, ModuleLoaderStatus } from "../interfaces/module-loader.interfaces";
import { ScreenType } from "../../location-search-map/models/ScreenType";
import { reportScreenSize } from "../../location-search-map/reportScreenSize";
import { initOnceWithDOMReady } from "../../util/dom-loader-check";

/**
 * DomLoader manages DOM manipulation and UI-related modules.
 * 
 * This loader is responsible for:
 * - Loading DOM manipulation utilities and libraries
 * - Managing UI component modules
 * - Handling interactive element functionality
 * - Loading client-side DOM enhancement modules
 * - Managing accessibility and UX improvement modules
 * 
 * The DomLoader follows the singleton pattern and implements the IModuleLoader
 * interface for consistent behavior across the router system.
 * 
 * Potential DOM Modules:
 * - Form validation and enhancement modules
 * - Interactive UI component libraries
 * - Accessibility improvement modules
 * - Animation and transition libraries
 * - DOM utility and helper modules
 * - Event handling and delegation systems
 * - Virtual DOM or rendering optimization modules
 * - Progressive enhancement modules
 * 
 * Use Cases:
 * - Loading form validation libraries when forms are present
 * - Adding interactive components based on page content
 * - Enhancing accessibility features dynamically
 * - Loading animation libraries for interactive elements
 * - Adding DOM utilities for complex page interactions
 * 
 * Features:
 * - Singleton pattern for global access
 * - Lightweight initialization (no special setup required)
 * - Secure module loading via RouterHelper
 * - Extensible architecture for new DOM modules
 * - Ready for future DOM enhancement requirements
 * 
 * @class DomLoader
 * @implements {IModuleLoader}
 */
export class DomLoader implements IModuleLoader {
    /** Singleton instance of the DomLoader */
    private static instance: DomLoader;

    /** RouterHelper instance for secure module loading operations */
    private readonly routerHelper: RouterHelper = RouterHelper.getInstance();

    /** Current status and state of the DomLoader */
    private readonly status: ModuleLoaderStatus = {
        isInitialized: false,
        loadedModules: [],
        failedModules: [],
        isLoading: false
    };

    /**
     * Gets the singleton instance of DomLoader.
     * Creates a new instance if one doesn't exist.
     * @returns The singleton DomLoader instance
     */
    static getInstance(): DomLoader {
        this.instance ??= new DomLoader();
        return this.instance;
    }

    /**
     * Initializes the DomLoader.
     * 
     * DOM modules typically don't require special initialization as they
     * work directly with the already-available DOM. This method simply
     * marks the loader as initialized and ready for module loading.
     * 
     * @returns Promise that resolves when initialization is complete
     * @throws {Error} When initialization fails
     */
    async init(): Promise<void> {
        try {
            // DOM modules don't need special initialization
            this.status.isInitialized = true;
        } catch (error) {
            this.status.lastError = error instanceof Error ? error.message : String(error);
            throw error;
        }
    }

    /**
     * Returns the human-readable name of this loader.
     * Used for logging, debugging, and status reporting.
     * @returns The loader's display name
     */
    getName(): string {
        return 'DomLoader';
    }

    /**
     * Returns a copy of the current loader status.
     * Provides real-time information about the loader's state.
     * @returns Current loader status information
     */
    getStatus(): ModuleLoaderStatus {
        return { ...this.status };
    }

    /**
     * Loads all DOM-related modules (delegates to domModules).
     * 
     * This is the main entry point for loading DOM modules,
     * implementing the IModuleLoader interface contract.
     * 
     * @returns Promise that resolves when all DOM modules are loaded
     */
    async loadModules(): Promise<void> {
        return this.domModules();
    }

    /**
     * Loads DOM manipulation and UI enhancement modules.
     * 
     * This method is designed to be extensible for future DOM-related modules.
     * Currently serves as a placeholder that can be expanded with specific
     * DOM module loading logic based on requirements.
     * 
     * Future Implementation Ideas:
     * - Detect forms and load validation libraries
     * - Scan for interactive elements and load enhancement modules
     * - Check for accessibility requirements and load a11y modules
     * - Detect animation needs and load animation libraries
     * - Load DOM utility libraries based on page complexity
     * 
     * Example Future Implementation:
     * ```typescript
     * // Check for forms and load validation
     * if (document.querySelector('form')) {
     *   await this.routerHelper.loadModuleSafely(
     *     'form-validation',
     *     () => import('./form-validation-module'),
     *     'Form Validation'
     *   );
     * }
     * 
     * // Check for interactive elements
     * if (document.querySelector('[data-interactive]')) {
     *   await this.routerHelper.loadModuleSafely(
     *     'ui-components',
     *     () => import('./ui-components-module'),
     *     'UI Components'
     *   );
     * }
     * ```
     * 
     * @returns Promise that resolves when DOM modules are loaded
     * @throws {Error} When DOM module loading fails
     */
    async domModules(): Promise<void> {
        this.status.isLoading = true;

        try {
            // DOM modules can be added here in the future           

            const modulesToLoad = [
                {
                    selector: '.insurance-form-section',
                    moduleName: 'Insuranceform',
                    importFn: async () => {
                        const mod = await import(
            /* webpackChunkName: "insurance-form-loader" */ '../../insurance/InsuranceForm'
                        );
                        if (mod.InsuranceForm) {
                            // Use the same getInstance pattern as the existing DOMContentLoaded listener
                            initOnceWithDOMReady('InsuranceForm', () => {
                                if (mod.InsuranceForm.getInstance) {
                                    // Use singleton getInstance if available
                                    mod.InsuranceForm.getInstance();
                                } else {
                                    // Fallback to constructor
                                    new mod.InsuranceForm();
                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: 'InsuranceForm'
                },
                {
                    selector: '.st-prequal-show',
                    moduleName: 'CustomerFinancing',
                    importFn: async () => {
                    const mod = await import(
        /* webpackChunkName: "customer-financing-loader" */ '../../financing/customer-financing'
                    );
                    if (mod.CustomerFinancing) {
                        // Use the same timing-safe pattern as InsuranceForm
                        initOnceWithDOMReady('CustomerFinancing', () => {
                            if ((globalThis as any).__CustomerFinancingInitialized) return;
                            if (mod.CustomerFinancing.getInstance) {
                                // Use singleton getInstance if available
                                mod.CustomerFinancing.getInstance();
                            } else {
                                // Fallback to constructor
                                new mod.CustomerFinancing();
                            }
                            (globalThis as any).__CustomerFinancingInitialized = true;
                        });
                    }
                    return mod;
                    },
                    instanceName: 'CustomerFinancing'
                },
                {
                   selector: '#insurance-confirmation',
                     moduleName: 'InsuranceConfirmation',
                        importFn: async () => {
                        const mod = await import(
            /* webpackChunkName: "insurance-confirmation-loader" */ '../../insurance/insuranceConfirmation'
                        );
                        if (mod.InsuranceConfirmation) {
                            // Use the same timing-safe pattern as InsuranceForm
                            initOnceWithDOMReady('InsuranceConfirmation', () => {
                                if (mod.InsuranceConfirmation.getInstance) {
                                    // Use singleton getInstance if available
                                    mod.InsuranceConfirmation.getInstance();
                                } else {
                                    // Fallback to constructor
                                    new mod.InsuranceConfirmation();
                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: 'InsuranceConfirmation'
                },
                {
                    selector: '#financing-form, #finance-confirmation',
                    moduleName: 'Financeform',
                    importFn: async () => {
                        const mod = await import(
            /* webpackChunkName: "finance-form-loader" */ '../../finance/index'
                        );
                        if (mod.FinancesService) {
                            // Use the same timing-safe pattern as other form modules
                            initOnceWithDOMReady('FinancesService', async () => {
                                 if ((globalThis as any).__FinancesServiceInitialized) return;
                                // Check for forms like the original IIFE does
                                const financeForms = document.getElementById('financing-form') || document.getElementById('finance-confirmation');
                                if (financeForms) {
                                    (globalThis as any).__FinancesServiceInitialized = true;
                                    if (mod.FinancesService?.create) {
                                        await mod.FinancesService.create();
                                    } else if (mod.FinancesService) {
                                        new mod.FinancesService([]);
                                    }
                                }

                                // Doing business as logic (preserve existing functionality)
                                const doingBusinessAs = localStorage.getItem('doingBusinessAs');
                                if (doingBusinessAs) {
                                    const el = document.querySelector('.finance-sub-heading');
                                    if (el) {
                                        el.classList.remove('hidden');
                                        el.innerHTML = doingBusinessAs;
                                    }
                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: 'FinancesService'
                }, {
                    selector: '.refer-wrapper',
                    moduleName: 'Referform',
                    importFn: async () => {
                        const mod = await import(
                    /* webpackChunkName: "refer-form-loader" */ '../../refer/index'
                        );
                        if (mod.ReferralService) {
                            // Use the same timing-safe pattern as InsuranceForm and InsuranceConfirmation
                            initOnceWithDOMReady('ReferralService', () => {
                                // Prevent multiple instantiations
                                if ((globalThis as any).__ReferralServiceInitialized) return;
                                const referralDom = document.querySelector('.refer-wrapper');
                                if (referralDom) {
                                    (globalThis as any).__ReferralServiceInitialized = true;
                                    // ReferralService uses constructor pattern
                                    new mod.ReferralService();
                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: 'ReferralService'
                }, {
                    selector: '.reward-form-section',
                    moduleName: 'RewardForm',
                    importFn: async () => {
                        const mod = await import(
                    /* webpackChunkName: "reward-form-loader" */ '../../reward/reward'
                        );
                        if (mod.RewardForm) {
                            // Use the same timing-safe pattern as other form modules
                            initOnceWithDOMReady('RewardForm', () => {
                                // RewardForm uses constructor pattern
                                if ((globalThis as any).__RewardFormInitialized) return;

                                const rewardSection = document.querySelector('.reward-form-section');
                                if (rewardSection) {
                                    (globalThis as any).__RewardFormInitialized = true;
                                    new mod.RewardForm();
                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: 'RewardForm'
                },{
                    selector: '#reward-confirmation',
                    moduleName: 'RewardConfirmation',
                    importFn: async () => {
                        const mod = await import(
                    /* webpackChunkName: "reward-confirmation-loader" */ '../../reward/rewardConfirmation'
                     );
                     if (mod.RewardConfirmation) {
                            // Use the same timing-safe pattern as other confirmation modules
                            initOnceWithDOMReady('RewardConfirmation', () => {
                                if ((globalThis as any).__RewardConfirmationInitialized) return;

                                (globalThis as any).__RewardConfirmationInitialized = true;
                                if (mod.RewardConfirmation.getInstance) {
                                    // Use singleton getInstance if available
                                    mod.RewardConfirmation.getInstance();
                                } else {
                                    // Fallback to constructor
                                    new mod.RewardConfirmation();
                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: 'RewardConfirmation'
                    },
                {
                    selector: '#ask-question-form',
                    moduleName: 'FormHandlerAskQuestion',
                    importFn: async () => {
                        const mod = await import(
                    /* webpackChunkName: "askquestion-form-loader" */ '../../ask-aquestion/index'
                        );
                        if (mod.FormHandlerAskQuestion) {
                            // Use the same timing-safe pattern as other form modules
                            initOnceWithDOMReady('FormHandlerAskQuestion', () => {
                                  if ((globalThis as any).__AskQuestionFormInitialized) {return;}
                                    (globalThis as any).__AskQuestionFormInitialized = true;
                                // FormHandlerAskQuestion uses singleton pattern
                                mod.FormHandlerAskQuestion.getInstance();
                            });
                        }
                        return mod;
                    },
                    instanceName: 'FormHandlerAskQuestion'
                }, {
                    selector: '#papers-download-form',
                    moduleName: 'FormHandlerAskQuestionWhitePaper',
                    importFn: async () => {
                        const mod = await import(
                    /* webpackChunkName: "whitepaper-form-loader" */ '../../white-paper/index'
                        );
                        if (mod.FormHandlerAskQuestionWhitePaper) {
                            // Use the same timing-safe pattern as other form modules
                            initOnceWithDOMReady('FormHandlerAskQuestionWhitePaper', () => {
                                 if ((globalThis as any).__WhitePaperFormInitialized) return;
                                 (globalThis as any).__WhitePaperFormInitialized = true;
                                // FormHandlerAskQuestionWhitePaper uses singleton pattern
                                mod.FormHandlerAskQuestionWhitePaper.getInstance();
                            });
                        }
                        return mod;
                    },
                    instanceName: 'FormHandlerAskQuestionWhitePaper'
                },
                {
                    selector: '.transformation-section.testimonials .transformation-carousel-wrap',
                    moduleName: 'ClientTransformCarousel',
                    importFn: async () => {
                        const mod = await import(
                    /* webpackChunkName: "clientCarousel-loader" */ '../../carouselVersion2/index'
                        );
                        if (mod.ClientTransformCarousel) {
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('ClientTransformCarousel', () => {
                                const instance = mod.ClientTransformCarousel.getInstance();
                                instance.getData();
                            });
                        }
                        return mod;
                    },
                    instanceName: 'ClientTransformCarousel'

                }, 
                {
                    selector: '.author-section',
                    moduleName: 'AuthorProfilePage',
                    importFn: async () => {
                        const mod = await import(
                    /* webpackChunkName: "authorprofile-loader" */ '../../components/index'
                        );
                        if (mod.AuthorProfilePage) {
                                // Use the same timing-safe pattern as other modules
                                initOnceWithDOMReady('AuthorProfilePage', () => {
                                    // Prevent duplicate initialization
                                    if ((globalThis as any).__AuthorProfilePageInitialized) return;
                                    const authorSection = document.querySelector('.author-section');
                                    if (authorSection) {
                                        (globalThis as any).__AuthorProfilePageInitialized = true;
                                        mod.AuthorProfilePage.getInstance();
                                    }
                                });
                        }
                        return mod;
                    },
                    instanceName: 'AuthorProfilePage'
                },
                {
                    selector: '.process-component',
                    moduleName: 'ProcessComponent',
                    importFn: async () => {
                        const mod = await import(
                    /* webpackChunkName: "process-component-loader" */ '../../components/processComponent'
                        );
                        if (mod.ProcessComponent) {
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('ProcessComponent', () => {
                                try {
                                    if ((globalThis as any).__ProcessComponentInitialized) return;
                                    if (document.querySelector('.process-steps')) {
                                        (globalThis as any).__ProcessComponentInitialized = true;
                                        mod.ProcessComponent.getInstance();
                                    }
                                } catch (error) {
                                    console.warn('ProcessComponent initialization failed:', error);
                                }
                            });
                        } else {
                            console.warn('ProcessComponent module loaded but ProcessComponent class not found');
                        }
                        return mod;
                    },
                    instanceName: 'ProcessComponent'
                },
                {
                    selector: '.new-calculator-section, .new-calculator-wrapper',
                    moduleName: 'CalculatorComponent',
                    importFn: async () => {         
                        const mod = await import(
                    /* webpackChunkName: "calculator-component-loader" */ '../../cust-fin/index'
                        );
                        if (mod.CalculatorComponent && !(globalThis as any).__CalculatorComponentDOMLoader) {
                            (globalThis as any).__CalculatorComponentDOMLoader = true;
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('CalculatorComponent', () => {
                                mod.CalculatorComponent.getInstance();
                            });
                        }
                        return mod;
                    },
                    instanceName: 'CalculatorComponent'
                }, {
                    selector: '.drip-form-wrap',
                    moduleName: 'DripCalculator',
                    importFn: async () => {
                        const mod = await import(
                    /* webpackChunkName: "dripcalculator-component-loader" */ '../../helperModules/dripCalculator'
                        );
                         if (mod.DripCalculator && !(globalThis as any).__DripCalculatorDOMLoader) {
                             (globalThis as any).__DripCalculatorDOMLoader = true;
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('DripCalculator', () => {
                                mod.DripCalculator.getInstance();
                            });
                        }
                        return mod;
                    },
                    instanceName: 'DripCalculator'
                },
                {

                    selector: '.deck-calculator-section',
                    moduleName: 'DeckCalculator',
                    importFn: async () => {
                        const mod = await import(
                    /* webpackChunkName: "deckcalculator-component-loader" */ '../../helperModules/deckCalculator'
                        );
                        if (mod.DeckCalculator) {
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('DeckCalculator', () => {
                                new mod.DeckCalculator();
                            });
                        }
                        return mod;
                    },
                    instanceName: 'DeckCalculator'
                }, {
                    selector: '.video-wrapper .video-container',
                    moduleName: 'MediaInjector',
                    importFn: async () => {
                        const mod = await import(
      /* webpackChunkName: "media-injector-loader" */ '../../video/video-component'
                        );
                        if (mod.MediaInjector && !(globalThis as any).__MediaInjectorInitialized) {
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('MediaInjector', () => {
                                new mod.MediaInjector();
                            });
                        }
                        return mod;
                    },
                    instanceName: 'MediaInjector'
                }, {
                    selector: '.video-container',
                    moduleName: 'YouTubeInit',
                    importFn: async () => {
                        const mod = await import(
      /* webpackChunkName: "youtube-init-loader" */ '../../video/index'
                        );
                         if (mod.youtubeInit) {
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('YouTubeInit', () => {
                                if ((globalThis as any).__YouTubeInitInitialized) {return;}
                                 (globalThis as any).__YouTubeInitInitialized = true;
                                mod.youtubeInit();
                            });
                        }
                        return mod;
                    },
                    instanceName: 'YouTubeInit'
                },
                {
                    selector: '.gallery-page-section.inspiration-gallery',
                    moduleName: 'InspirationGallery',
                    importFn: async () => {
                        const mod = await import(
      /* webpackChunkName: "inspiration-gallery-loader" */ '../../photo_gallery/inspirationGallery'
                        );
                        if (mod.InspirationGallery) {
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('InspirationGallery', () => {
                                if ((globalThis as any).__InspirationGalleryInitialized){ return;}
                                (globalThis as any).__InspirationGalleryInitialized = true;
                                mod.InspirationGallery.getInstance();
                            });
                        }
                        return mod;
                    },
                    instanceName: 'InspirationGallery'
                }, 
                {
                    selector: '.gallery-page-section',
                    moduleName: 'PhotoGallery2',
                    importFn: async () => {
                        const mod = await import(
      /* webpackChunkName: "photogallery2-loader" */ '../../photo_gallery/photogallery2'
                        );
                        if (mod.PhotoGallery2) {
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('PhotoGallery2', () => {
                                if (!(globalThis as any).__PhotoGallery2Initialized) {
                                    mod.PhotoGallery2.getInstance();
                                    (globalThis as any).__PhotoGallery2Initialized = true;
                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: 'PhotoGallery2'
                }, 
                {
                    selector: '.sp-offers-section',
                    moduleName: 'OffersLanding',
                    importFn: async () => {
                        const mod = await import(
      /* webpackChunkName: "offers-landing-loader" */ '../../offers/offers-landing'
                        );

                        if (mod.OffersLanding) {
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('OffersLanding', () => {
                                if (!(globalThis as any).__OffersLandingInitialized) {
                                    (globalThis as any).__OffersLandingInitialized = true;
                                    const instance = mod.OffersLanding.getInstance();
                                    instance?.attachPrintEvntListner();
                                    instance?.attachCloseDrawerListener();
                                    instance?.readmoreFunctionality();
                                    instance?.attachEventListnerForClaim();

                                    const section = document.querySelector(".sp-offers-section");
                                    if (section && !section.classList.contains('coupons-eight')) {
                                        instance?.init();
                                    }

                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: 'OffersLanding'
                }, {
                    selector: '.offers-block-section.dynamic-offer-teaser-component',
                    moduleName: 'OffersTeaser',
                    importFn: async () => {
                        const mod = await import(
      /* webpackChunkName: "offers-teaser-loader" */ '../../offers/offers-teaser'
                        );

                        if (mod.OffersTeaser) {
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('OffersTeaser', () => {
                                if (!(globalThis as any).__OffersTeaserLoaded) {
                                    (globalThis as any).__OffersTeaserLoaded = true;
                                    const offersBlock = document.querySelector(
                                        '.offers-block-section.dynamic-offer-teaser-component .offers-block-wrapper .offers-block-list'
                                    );

                                    if (offersBlock) {
                                        mod.OffersTeaser.getInstance();
                                    }
                                }
                            });
                        }

                        return mod;
                    },
                    instanceName: 'OffersTeaser'
                }, {
                    selector: '#sp-offer-new',
                    moduleName: 'SpecialOfferPopUp',
                    importFn: async () => {
                        const mod = await import(
      /* webpackChunkName: "special-offer-popup-loader" */ '../../offers/special-offer-popup'
                        );

                        if (mod.SpecialOfferPopUp) {
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('SpecialOfferPopUp', () => {
                                const specialOfferMarkupDom = document.getElementById('sp-offer-new');
                                if (specialOfferMarkupDom) {
                                    if (!(globalThis as any).__SpecialOfferPopUpInitialized) {
                                        (globalThis as any).__SpecialOfferPopUpInitialized = true;
                                        mod.SpecialOfferPopUp.create();
                                    }
                                }
                            });
                        }

                        return mod;
                    },
                    instanceName: 'SpecialOfferPopUp'
                },
                {
                    selector: '.gift-cetificate-wrap',
                    moduleName: 'GiftCardFormHandler',
                    importFn: async () => {
                        const mod = await import(
      /* webpackChunkName: "getgiftcard-loader" */ '../../moly-gift/index'
                        );
                        if (mod.GiftCardFormHandler) {
                            // DOM is already ready, original DOMContentLoaded won't fire
                            initOnceWithDOMReady('GiftCardFormHandler', () => {
                                // prevents duplicate initialization
                                if (!(globalThis as any).__GiftCardFormHandlerInitialized) {
                                    (globalThis as any).__GiftCardFormHandlerInitialized = true;
                                    mod.GiftCardFormHandler.getInstance();
                                }
                            });

                        }
                        return mod;
                    },
                    instanceName: 'GiftCardFormHandler'
                },
                {
                    selector: '.blog-section',
                    moduleName: 'BlogList',
                    importFn: async () => {
                        const mod = await import(
                    /* webpackChunkName: "blog-loader" */ '../../blog/index'
                        );
                        
                        if (mod.BlogList) {
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('BlogList', async () => {
                                // Create blog instance - constructor will set up event listeners
                                const blogInstance = new mod.BlogList();

                                // Since DOMContentLoaded has already fired, manually trigger initialization
                                // by dispatching a custom event or calling initialization directly
                                const conceptCode = (document.getElementById('conceptCode') as HTMLInputElement)?.value;
                                if (conceptCode) {
                                    // Import the utility function directly
                                    const { getBrandDetailsFromBrandJson } = await import('../../util/share');
                                    const brandData = await getBrandDetailsFromBrandJson(conceptCode);
                                    (blogInstance as any).brandDetail = brandData;

                                    const blogContainer = document?.querySelector('.blog-list');
                                    if (blogContainer && brandData && !brandData.enable_fte) {
                                        // Call getData method if available
                                        if (typeof (blogInstance as any).getData === 'function') {
                                            (blogInstance as any).getData(null);
                                        }
                                    }
                                }

                            });
                        }
                        
                        // Manually trigger NextPrev if available (since DOMContentLoaded already fired)
                        if (mod.NextPrev) {
                            // Use the same timing-safe pattern for NextPrev
                            initOnceWithDOMReady('NextPrev', async () => {
                                await mod.NextPrev.init();
                            });
                        }

                        return mod;
                    },
                    instanceName: 'BlogList'
                },
                {
                    selector: '.ai-summarize-block',
                    moduleName: 'BlogAiSummary',
                    importFn: async () => {
                        const mod = await import(
                            /* webpackChunkName: "blog-ai-summary-loader" */ '../../blog/ai-summary'
                        );
                        if (mod.BlogAiSummary) {
                            initOnceWithDOMReady('BlogAiSummary', () => {
                                try {
                                    mod.BlogAiSummary.getInstance().init();
                                } catch (error) {
                                    console.warn('BlogAiSummary initialization failed:', error);
                                }
                            });
                        } else {
                            console.warn('BlogAiSummary module loaded but BlogAiSummary class not found');
                        }
                        return mod;
                    },
                    instanceName: 'BlogAiSummary'
                },
                {
                    selector: '.service-category-nav',
                    moduleName: 'ServiceCategoryTiles',
                    importFn: async () => {
                        const mod = await import(
                            /* webpackChunkName: "service-category-tiles-loader" */ '../../components/serviceCategoryTiles'
                        );
                        if (mod.ServiceCategoryTiles) {
                            initOnceWithDOMReady('ServiceCategoryTiles', () => {
                                try {
                                    mod.ServiceCategoryTiles.getInstance();
                                } catch (error) {
                                    console.warn('ServiceCategoryTiles initialization failed:', error);
                                }
                            });
                        } else {
                            console.warn('ServiceCategoryTiles module loaded but ServiceCategoryTiles class not found');
                        }
                        return mod;
                    },
                    instanceName: 'ServiceCategoryTiles'
                },
                {
                    selector: '.ai-share-dropdown',
                    moduleName: 'AiShareToggle',
                    importFn: async () => {
                        const mod = await import(
                            /* webpackChunkName: "ai-share-toggle-loader" */ '../../blog/aiShareToggle'
                        );
                        if (mod.AiShareToggle) {
                            initOnceWithDOMReady('AiShareToggle', () => {
                                try {
                                    mod.AiShareToggle.getInstance().init();
                                } catch (error) {
                                    console.warn('AiShareToggle initialization failed:', error);
                                }
                            });
                        } else {
                            console.warn('AiShareToggle module loaded but AiShareToggle class not found');
                        }
                        return mod;
                    },
                    instanceName: 'AiShareToggle'
                },
                {
                    selector: '.hero-review',
                    moduleName: 'HeroComponent',
                    importFn: async () => {
                        const mod = await import(
            /* webpackChunkName: "hero-component-loader" */ '../../hero_component/heroComponent'
                        );
                        if (mod.HeroComponent) {
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('HeroComponent', async () => {
                                if (!(globalThis as any).__HeroComponentInitialized) {
                                    (globalThis as any).__HeroComponentInitialized = true;
                                    await mod.HeroComponent.create();
                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: 'HeroComponent'
                }, 

                {
                    selector: '.residential-form.mobile-contact-us',
                    moduleName: 'MobileContactUs',
                    importFn: async () => {
                        const mod = await import(
            /* webpackChunkName: "mobile-contact-us-loader" */ '../../hero_component/contactUsMobile'
                        );
                        if (mod.MobileContactUs) {
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('MobileContactUs', () => {
                                if ((globalThis as any).__MobileContactUsInitialized) {return;}
                                const screenType = reportScreenSize?.(); // Only call if available
                                if (screenType !== ScreenType.Desktop) {
                                    mod.MobileContactUs.getInstance();
                                     (globalThis as any).__MobileContactUsInitialized = true;
                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: 'MobileContactUs'
                },
                {
                    selector: '.sp-offers-list .sp-offers-item .sp-offers-cta .secondary-btn',
                    moduleName: 'Print',
                    importFn: async () => {
                        const mod = await import(
      /* webpackChunkName: "print-offer-loader" */ '../../print/print'
                        );
                        if (mod.Print && document.readyState !== 'loading') {
                            // DOM is already ready, original DOMContentLoaded won't fire
                            initOnceWithDOMReady('Print', () => {
                                initializePrintModule(mod.Print);
                            });
                        }
                        return mod;
                    },
                    instanceName: 'Print'
                },
                {
                    selector: '#header-alert',
                    moduleName: 'BannerManager',
                    importFn: async () => {
                        const mod = await import(
      /* webpackChunkName: "banner-manager-loader" */ '../../header/information-banner'
                        );
                        if (mod.BannerManager) {
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('BannerManager', () => {
                                mod.BannerManager.getInstance();
                            });
                        }
                        return mod;
                    },
                    instanceName: 'BannerManager'
                },
                {
                    selector: '.contact-info form',
                    moduleName: 'FormHandler',
                    importFn: async () => {
                        // Set coordination flags BEFORE importing to prevent IIFE conflicts                        
                        const mod = await import(
      /* webpackChunkName: "form-handler-loader" */ '../../contact_us/contactus'
                        );

                        // Handle DOMContentLoaded timing for dynamic chunk loading
                        // The contactus.ts file has DOMContentLoaded listeners that won't fire when loaded as chunk
                        initOnceWithDOMReady('ContactUsFormHandler', async () => {
                            if (!(globalThis as any).__ContactUsFormHandlerDOMLoader) {
                                (globalThis as any).__ContactUsFormHandlerDOMLoader = true; 
                                await initializeContactUsFormHandler(mod);
                            }
                        });

                        return mod;
                    },
                    instanceName: 'FormHandler'
                },
                 {
                     selector: '.contact-info .contact-block .get_in_touch_hrs_operation',
                     moduleName: 'GetHoursOfOperation',
                     importFn: async () => {                       
                         const mod = await import(
            /* webpackChunkName: "hours-of-operation-loader" */ '../../contact_us/get_HoursofOperation'
                         );
                         if (mod.GetHoursOfOperation) {            
                             // Use the same timing-safe pattern as other modules
                             initOnceWithDOMReady('GetHoursOfOperation', async () => {
                                 if (!(globalThis as any).__GetHoursOfOperationDOMLoader){
                                 (globalThis as any).__GetHoursOfOperationDOMLoader = true;
                                    await mod.GetHoursOfOperation.create();
                                 }
                                });
                         }
                         return mod;
                     },
                     instanceName: 'GetHoursOfOperation'
                 },
                 {
                     selector: '.donation-form, .donation-success',
                     moduleName: 'Donation',
                     importFn: async () => {
                         const mod = await import(
                             /* webpackChunkName: "donation-loader" */ '../../mly/donation/donation'
                         );
                         
                         // Donation module has DOMContentLoaded listener with singleton pattern and DOM element checks
                         // It checks for conceptId === 1 and presence of .donation-form or .donation-success elements
                         // The original module has auto-instantiation logic based on DOM elements
                         
                         if (mod.Donation) {
                             if (document.readyState !== 'loading') {
                                 // DOM is already ready, original DOMContentLoaded won't fire
                                 initOnceWithDOMReady('Donation', async () => {
                                     if (!(globalThis as any).__DonationDOMLoader) {
                                         (globalThis as any).__DonationDOMLoader = true;
                                         const conceptIdInput = document.getElementById('conceptId') as HTMLInputElement;
                                         if (conceptIdInput && Number.parseInt(conceptIdInput.value) === 1) {
                                             const donationFormDom = document.querySelector(".donation-form");
                                             const donationConfirmationDom = document.querySelector(".donation-success");

                                             if (donationFormDom) {
                                                  mod.Donation.getInstance();
                                                 // Initialize dropdowns when DOM is stable in chunked loading
                                             } else if (donationConfirmationDom) {
                                                 const currentUrl = globalThis.location.href;
                                                 const params = Object.fromEntries(new URLSearchParams(globalThis.location.search).entries());
                                                 const resultMsg = params.result_msg;
                                                 mod.donationConfirmation(currentUrl, resultMsg);
                                             }
                                         }
                                     }
                                 });
                             }
                         }
                         
                         return mod;
                     },
                     instanceName: 'Donation'
                 },
                 {
                     selector: '.gift-certificate-form, .gift-add-certificate-form, #gc-recipients-details',
                     moduleName: 'GiftRecipient',
                     importFn: async () => {
                         const [giftRecipientMod, giftPurchaseMod] = await Promise.all([
                             import(/* webpackChunkName: "gift-recipient-loader" */ '../../mly/gift-recipient'),
                             import(/* webpackChunkName: "gift-purchase-loader" */ '../../mly/gift-purchase')
                         ]);
                         
                         // It checks for gift-certificate URLs and handles both form and confirmation flows
                         // Original module creates both GiftRecipient and GiftPurchase instances                 
                         if (giftRecipientMod.GiftRecipient) {
                             const initializeGiftFlow = () => {
                                if ((globalThis as any).__GiftFlowInitialized) return;
                                // Handle form page
                                giftRecipientMod.GiftRecipient.getInstance();
                                giftPurchaseMod.GiftPurchase.getInstance();
                                this.routerHelper.routerConsoleLog(
                                    `GiftRecipient and GiftPurchase loaded successfully`,
                                    false,
                                    "DomLoader"
                                );
                                // Mark initialized
                                (globalThis as any).__GiftFlowInitialized = true;
                             };

                             if (document.readyState === 'loading') {
                                 // Wait for DOMContentLoaded if DOM is still loading
                                 document.addEventListener('DOMContentLoaded', initializeGiftFlow, { once: true });
                             } else {
                                 // Initialize immediately when DOM is ready
                                 initializeGiftFlow();
                             }
                         }
                         
                         return giftRecipientMod;
                     },
                     instanceName: 'GiftRecipient'
                 },
                 {
                     selector: '.gift-purchase-msg',
                     moduleName: 'GiftConfirmation',
                     importFn: async () => {
                        const giftConfirmationMod = await import(/* webpackChunkName: "gift-confirmation-loader" */ '../../mly/gift-confirmation');
                         
                         // It checks for gift-certificate URLs and handles both form and confirmation flows
                         // Original module creates both GiftRecipient and GiftPurchase instances                 
                         if (giftConfirmationMod.GiftConfirmation) {
                             const initializeGiftConfirmation = () => {
                                 if ((globalThis as any).__GiftConfirmationFlowInitialized) return;
                                 const currentUrl = globalThis.location.href;
                                 const urlParams = new URLSearchParams(globalThis.location.search);
                                 const params = Object.fromEntries(urlParams.entries());
                                 const resultMsg = params.result_msg;
                                 const isCertification = currentUrl.includes("gift-certificate");

                                 if (globalThis.location.pathname.endsWith("confirmation/") && isCertification) {
                                     const giftConfirmation = new giftConfirmationMod.GiftConfirmation();

                                     if (currentUrl.includes("?")) {
                                         const isApproved = resultMsg?.toUpperCase() === "APPROVAL";
                                         if (isApproved) {
                                             globalThis.sessionStorage.setItem('giftCertPaymentResult', resultMsg);
                                         } else {
                                             globalThis.sessionStorage.removeItem('giftCertPaymentResult');
                                         }
                                         giftConfirmation.displayMessage(isApproved);
                                         giftConfirmation.pushHTMLToHistoryAndCleanUrl(isApproved);
                                     } else {
                                         const storedResultMsg = globalThis.sessionStorage.getItem('giftCertPaymentResult');
                                         giftConfirmation.displayMessage(!!storedResultMsg);
                                     }

                                        // Restores the success/failure message on back/forward navigation without
                                        // replacing the DOM and destroying existing event listeners.
                                     globalThis.onpopstate = (event: PopStateEvent) => {
                                         giftConfirmation.restoreFromHistoryState(event.state);
                                     };
                                 }
                                 (globalThis as any).__GiftConfirmationFlowInitialized = true;
                             };

                             if (document.readyState === 'loading') {
                                 document.addEventListener('DOMContentLoaded', initializeGiftConfirmation, { once: true });
                             } else {
                                 initializeGiftConfirmation();
                             }
                         } 
                         
                         return giftConfirmationMod;
                     },
                     instanceName: 'GiftConfirmation'
                 },
                 {
                    selector: '.unsubscribe-form-section',
                    moduleName: 'UnsubscribeForm',
                    importFn: async () => {                       
                        const mod = await import(
                            /* webpackChunkName: "unsubscribe-form-loader" */ '../../unsubscribe-form/UnsubscribeForm'
                        );
                        if (mod.UnsubscribeForm) {            
                            // Use the same timing-safe pattern as other modules
                            initOnceWithDOMReady('UnsubscribeForm', () => {
                                if (!(globalThis as any).__UnsubscribeFormInitialized){
                                    (globalThis as any).__UnsubscribeFormInitialized = true;
                                    mod.UnsubscribeForm.create();
                                    this.routerHelper.routerConsoleLog(
                                        `UnsubscribeForm loaded successfully`,
                                        false,
                                        "DomLoader"
                                    );
                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: 'UnsubscribeForm'
                 },
                 {
                    selector: '.search-result-hero-banner',
                    moduleName: 'BrandResult',
                    importFn: async () => {                       
                        const mod = await import(
                            /* webpackChunkName: "search-result-form-loader" */ '../../search-result/brand-result'
                        );
                        if (mod.BrandResult) {            
                            initOnceWithDOMReady('BrandResult', () => {
                                if (!(globalThis as any).__SearchResultFormInitialized){
                                    (globalThis as any).__SearchResultFormInitialized = true;
                                    mod.BrandResult.getInstance();
                                }
                            });
                        }
                        return mod;
                    },
                    instanceName: 'BrandResult'
                 },
               
                 {
                    selector: '.google-preferred-bage',
                    moduleName: 'GooglePreferredBadge',
                    importFn: async () => {
                        const mod = await import(
                            /* webpackChunkName: "google-preferred-badge-loader" */ '../../util/googlePreferredBadge'
                        );
                        if (mod.GooglePreferredBadge) {
                            initOnceWithDOMReady('GooglePreferredBadge', () => {
                                try {
                                    mod.GooglePreferredBadge.getInstance().init();
                                } catch (error) {
                                    console.warn('GooglePreferredBadge initialization failed:', error);
                                }
                            });
                        } else {
                            console.warn('GooglePreferredBadge module loaded but GooglePreferredBadge class not found');
                        }
                        return mod;
                    },
                    instanceName: 'GooglePreferredBadge'
                 },
                 {
                    selector: '.share-and-google-cta-wrapper',
                    moduleName: 'ShareGoogleCtaPlacement',
                    importFn: async () => {
                        const mod = await import(
                            /* webpackChunkName: "share-google-cta-placement-loader" */ '../../blog/shareGoogleCtaPlacement'
                        );
                        if (mod.ShareGoogleCtaPlacement) {
                            initOnceWithDOMReady('ShareGoogleCtaPlacement', () => {
                                try {
                                    mod.ShareGoogleCtaPlacement.getInstance().init();
                                } catch (error) {
                                    console.warn('ShareGoogleCtaPlacement initialization failed:', error);
                                }
                            });
                        } else {
                            console.warn('ShareGoogleCtaPlacement module loaded but ShareGoogleCtaPlacement class not found');
                        }
                        return mod;
                    },
                    instanceName: 'ShareGoogleCtaPlacement'
                 },
                 {
                    selector: '.logo-farm-tile-v2',
                    moduleName: 'LogoTileSectionV2',
                    importFn: async () => {
                        const mod = await import(
                            /* webpackChunkName: "logo-tile-farm-loader" */ '../../logo-tile-farm/LogoTileFarmV2'
                        );
                        if (mod.LogoTileSectionV2) {
                            initOnceWithDOMReady('LogoTileSectionV2', () => {
                                try {
                                    if ((globalThis as any).__LogoTileSectionV2Initialized) return;
                                    (globalThis as any).__LogoTileSectionV2Initialized = true;
                                    mod.LogoTileSectionV2.create();
                                } catch (error) {
                                    console.warn('LogoTileSectionV2 initialization failed:', error);
                                }
                            });
                        } else {
                            console.warn('LogoTileSectionV2 module loaded but LogoTileSectionV2 class not found');
                        }
                        return mod;
                    },
                    instanceName: 'LogoTileSectionV2'
                 },
            ]
            for (const mod of modulesToLoad) {
                const el = document.querySelector(mod.selector);
                if (el) {
                    await this.routerHelper.loadModuleSafely(
                        mod.moduleName,
                        mod.importFn,
                        mod.instanceName
                    );
                    this.status.loadedModules.push(mod.moduleName);

                    this.routerHelper.routerConsoleLog(
                        `${mod.moduleName} loaded for ${mod.selector}`,
                        false,
                        'DomLoader'
                    );
                }
            }

            if (this.status.loadedModules.length === 0) {
                this.routerHelper.routerConsoleLog(
                    'DOM modules loading completed (no modules found)',
                    false,
                    'DomLoader'
                );
            }
        } catch (error) {
            this.status.lastError = error instanceof Error ? error.message : String(error);
            throw error;
        } finally {
            this.status.isLoading = false;
        }
    }
}


// Helper functions to reduce nesting depth in FormHandler module

/**
 * Sets up file input listeners for contact form
 */
function setupFileInputListeners(): void {
    const fileInputs = document.querySelectorAll('input[type="file"]');
    fileInputs.forEach((fileInput, index) => {
        const newFileInput = fileInput.cloneNode() as HTMLInputElement;
        fileInput.parentNode?.replaceChild(newFileInput, fileInput);

        newFileInput.addEventListener('change', () => {
            document.dispatchEvent(
                new CustomEvent('filePreviewNeeded', {
                    detail: { fileInput: newFileInput, index }
                })
            );
        });
    });
}

/**
 * Sets up close icon listeners for contact form
 */
function setupCloseIconListeners(): void {
    const closeIcons = document.querySelectorAll('.icon-close');
    closeIcons.forEach((close, index) => {
        const newClose = close.cloneNode(true) as HTMLElement;
        close.parentNode?.replaceChild(newClose, close);

        newClose.addEventListener('click', () => {
            const event = new CustomEvent('clearPreviewNeeded', { detail: { index } });
            document.dispatchEvent(event);
        });
    });
}

/**
 * Handles disclaimer visibility for CA/US regions
 */
async function handleDisclaimerVisibility(): Promise<void> {
    const { DomainIdentifier } = await import('../../util/domainService');
    const { getCountryCode } = await import('../../location-search-map/getCountryCode');
    
    const localisation = DomainIdentifier();
    const countryCode = getCountryCode();
    const localisedcountryCode = sessionStorage.getItem('countryCode');
    
    if (localisation === 'ca' || countryCode === 'ca' || localisedcountryCode === 'ca') {
        const countryZip = (localisation === 'ca' || countryCode === 'ca') ? 'ca' : 'us';
        const event = new CustomEvent('toggleDisclaimerNeeded', { 
            detail: { countryZip, zipCode: '' } 
        });
        document.dispatchEvent(event);
    }
}

/**
 * Handles DOMContentLoaded functionality for contact form
 */
async function handleContactFormDOMContent(): Promise<void> {
    if ((globalThis as any).__ContactUsDOMContentHandled) {
        return;
    }
    (globalThis as any).__ContactUsDOMContentHandled = true;
    
    const formHandler = document.querySelector('.contact-info form');
    if (!formHandler) {
        return;
    }

    // Setup file input listeners if not in scheduler or appointment context
    const containerImages = document.querySelector('.scheduler-main');
    const isApp = globalThis.location.pathname.includes('schedule-appointment');
    if (!containerImages && !isApp) {
        setupFileInputListeners();
        setupCloseIconListeners();
    }

    await handleDisclaimerVisibility();
}

/**
 * Initializes ContactUs FormHandler with reduced nesting
 */
async function initializeContactUsFormHandler(mod: any): Promise<void> {
    // Check if it's not ask a question form
    if (document.getElementById('ask-question-form')) {
        return;
    }

    // Coordinate with contactus.ts IIFE to prevent double creation
    (globalThis as any).__ContactUsFormHandlerDOMLoader = true;
    
    // Check if IIFE already created instance
    const alreadyCreated = (globalThis as any).__ContactUsFormHandlerCreated;
    
    if (!alreadyCreated) {
        await mod.FormHandler.create();
        (globalThis as any).__ContactUsFormHandlerCreated = true;
    }

    await handleContactFormDOMContent();
}


// Helper function to reduce nesting
function initializePrintModule(PrintClass: any) {
    const printInstance = new PrintClass();
    // Manually attach print event listeners since DOMContentLoaded won't fire
    const printBtns = document.querySelectorAll('.sp-offers-list .sp-offers-item .sp-offers-cta .secondary-btn');
    if (printBtns) {
        printBtns.forEach((printBtn: any, index) => {
            // Only attach if NOT inside the special section
            const parentSection = printBtn.closest('section.sp-offers-section.coupons-section.fte-special-offers-list');
            if (!parentSection && !(printBtn?.getAttribute('href') !== '' && printBtn?.getAttribute('href') !== '#')) {
                printBtn.addEventListener('click', (e: any) => printInstance.printOffer(e, index));
            }
        });
    }
}


